import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  MenuItem,
  Chip,
  Avatar,
  Select,
  Autocomplete,
} from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import api from '../../utils/api';
import MemberProfileAvatar from '../../components/MemberProfileAvatar';
import PageHeader from '../../components/Layout/PageHeader';
import { surfaceCardSx, tokens } from '../../theme/tokens';

/** Normalize stored date / month string for HTML date input (YYYY-MM-DD) */
function toDateInputValue(raw) {
  if (!raw) return '';
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  if (/^\d{4}-\d{2}$/.test(s.slice(0, 7))) return `${s.slice(0, 7)}-01`;
  return '';
}

export default function MemberForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cardStatus, setCardStatus] = useState(null);
  const [creatingCard, setCreatingCard] = useState(false);
  const [approvingCard, setApprovingCard] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [constituencies, setConstituencies] = useState([]);
  const [loadingConstituencies, setLoadingConstituencies] = useState(true);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    gender: '',
    constituency: '',
    status: 'Pending',
    student_id_number: '',
    school_completion_date: '',
  });

  useEffect(() => {
    if (id) {
      fetchMember();
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingConstituencies(true);
        const res = await api.get('/member-portal/constituencies');
        if (!cancelled) setConstituencies(res.data.data || []);
      } catch {
        if (!cancelled) setConstituencies([]);
      } finally {
        if (!cancelled) setLoadingConstituencies(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const constituencyOptions = useMemo(
    () => constituencies.map((c) => c.name).filter(Boolean),
    [constituencies]
  );

  const fetchMember = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/members/${id}`);
      setFormData(response.data.data);
      const cardRes = await api.get(`/members/${id}/card`).catch(() => ({ data: {} }));
      setCardStatus(cardRes.data?.data?.status ?? null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch member');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCardRecord = async () => {
    if (!id) return;
    try {
      setCreatingCard(true);
      setError('');
      await api.post(`/members/${id}/create-card`);
      setCardStatus('pending');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create card record');
    } finally {
      setCreatingCard(false);
    }
  };

  const handleApproveCard = async () => {
    if (!id) return;
    try {
      setApprovingCard(true);
      setError('');
      await api.post(`/members/${id}/card/approve`);
      setCardStatus('approved');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to approve card');
    } finally {
      setApprovingCard(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePictureChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.match(/^image\/(jpeg|jpg|png|gif)$/i)) {
      setError('Please select a JPEG, PNG, or GIF image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }
    setError('');
    setProfilePicture(file);
    const reader = new FileReader();
    reader.onloadend = () => setProfilePicturePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemovePicture = async () => {
    if (!id) return;
    if (!window.confirm('Remove this member profile photo completely?')) return;
    try {
      setLoading(true);
      setError('');
      await api.delete(`/members/${id}/profile-picture`);
      setProfilePicture(null);
      setProfilePicturePreview(null);
      await fetchMember();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove profile image');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (id) {
        await api.put(`/members/${id}`, formData);
        if (profilePicture) {
          const photoData = new FormData();
          photoData.append('profile_picture', profilePicture);
          await api.post(`/members/${id}/profile-picture`, photoData);
        }
        // Refetch card status when status is Active (backend auto-approves card)
        if (formData.status === 'Active') {
          const cardRes = await api.get(`/members/${id}/card`).catch(() => ({}));
          setCardStatus(cardRes.data?.data?.status ?? 'approved');
        }
      } else {
        const fd = new FormData();
        const fields = [
          'first_name',
          'last_name',
          'email',
          'phone',
          'gender',
          'constituency',
          'status',
          'student_id_number',
          'school_completion_date',
        ];
        fields.forEach((key) => {
          const v = formData[key];
          if (v !== undefined && v !== null && String(v).trim() !== '') {
            fd.append(key, typeof v === 'string' ? v : String(v));
          }
        });
        if (profilePicture) {
          fd.append('profile_picture', profilePicture);
        }
        const res = await api.post('/members', fd);
        const noEmail = !String(formData.email || '').trim();
        setCreateSuccess({
          message: res.data?.message || 'Member created.',
          temporaryPassword: res.data?.temporaryPassword,
          welcomeEmailSent: res.data?.welcomeEmailSent,
          noEmail,
        });
        return;
      }
      navigate('/dashboard/members');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save member');
    } finally {
      setLoading(false);
    }
  };

  if (loading && id) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (createSuccess && !id) {
    return (
      <Box>
        <PageHeader
          crumbs={
            <>
              Registry / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Members</Box>
            </>
          }
          title="Member created"
        />
        <Paper elevation={0} sx={{ ...surfaceCardSx, p: 3, maxWidth: 560 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            {createSuccess.message}
          </Alert>
          {createSuccess.welcomeEmailSent && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              They can sign in at the member portal using the email you entered and the password sent to them.
            </Typography>
          )}
          {!createSuccess.welcomeEmailSent && createSuccess.noEmail && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No email was saved for this member. Share the temporary password with them; they can sign in at the member
              portal using their phone number.
            </Typography>
          )}
          {createSuccess.temporaryPassword && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Temporary password (only shown here if email was not sent)
              </Typography>
              <TextField
                fullWidth
                value={createSuccess.temporaryPassword}
                InputProps={{ readOnly: true }}
                onFocus={(e) => e.target.select()}
              />
            </Box>
          )}
          <Button variant="contained" onClick={() => navigate('/dashboard/members')}>
            Back to members
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        crumbs={
          <>
            Registry / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>{id ? 'Edit Member' : 'Add New Member'}</Box>
          </>
        }
        title={id ? 'Edit Member' : 'Add New Member'}
      />
      <Paper elevation={0} sx={{ ...surfaceCardSx, p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Profile photo (optional)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {profilePicturePreview ? (
                  <Avatar src={profilePicturePreview} sx={{ width: 96, height: 96, bgcolor: 'primary.main', fontSize: '2rem' }}>
                    {formData.first_name?.[0] || 'M'}
                  </Avatar>
                ) : (
                  <MemberProfileAvatar member={formData} size={96} />
                )}
                <Box>
                  <input
                    accept="image/jpeg,image/jpg,image/png,image/gif"
                    style={{ display: 'none' }}
                    id="admin-member-photo"
                    type="file"
                    onChange={handlePictureChange}
                  />
                  <label htmlFor="admin-member-photo">
                    <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />} size="small">
                      {profilePicture ? 'Change photo' : 'Upload photo'}
                    </Button>
                  </label>
                  {id && (formData?.profile_picture || profilePicturePreview) && (
                    <Button
                      variant="text"
                      color="error"
                      size="small"
                      onClick={handleRemovePicture}
                      sx={{ ml: 1 }}
                    >
                      Remove photo
                    </Button>
                  )}
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    JPEG, PNG, or GIF, max 5MB. {id ? 'Saved when you click Update Member.' : 'Same as member self-registration.'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                helperText="Optional for now — member can sign in with phone if omitted"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                required
              >
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                loading={loadingConstituencies}
                options={constituencyOptions}
                value={formData.constituency ?? ''}
                onChange={(e, newValue) => {
                  setFormData((prev) => ({ ...prev, constituency: newValue ?? '' }));
                }}
                inputValue={formData.constituency ?? ''}
                onInputChange={(e, newInputValue, reason) => {
                  if (reason === 'input' || reason === 'clear' || reason === 'reset') {
                    setFormData((prev) => ({ ...prev, constituency: newInputValue }));
                  }
                }}
                filterOptions={(opts, { inputValue: q }) => {
                  const needle = q.trim().toLowerCase();
                  if (!needle) return opts.slice(0, 120);
                  return opts.filter((o) => o.toLowerCase().includes(needle)).slice(0, 120);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="constituency"
                    label="Constituency"
                    helperText="Type to filter the list, pick from suggestions, or enter any constituency"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="School / Student ID"
                name="student_id_number"
                value={formData.student_id_number || ''}
                onChange={handleChange}
                helperText="Institution student or school ID (required for registration)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Status"
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Suspended">Suspended</MenuItem>
                <MenuItem value="Withdrawn">Withdrawn</MenuItem>
                <MenuItem value="Alumni">Alumni</MenuItem>
                <MenuItem value="Deceased">Deceased</MenuItem>
                <MenuItem value="Students">Students</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expected completion date"
                name="school_completion_date"
                type="date"
                value={toDateInputValue(formData.school_completion_date)}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    school_completion_date: v || '',
                  }));
                }}
                InputLabelProps={{ shrink: true }}
                helperText={
                  id
                    ? formData.status === 'Alumni'
                      ? 'Use the calendar to set completion when reactivating (re-registration).'
                      : 'Choose a date from the calendar. Used for ID card expiry and alumni rules.'
                    : 'Open the calendar to pick a date (same idea as member registration).'
                }
              />
            </Grid>
            {formData.status === 'Alumni' && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'info.light', borderColor: 'info.main' }}>
                  <Typography variant="subtitle2" color="info.dark" gutterBottom>
                    Re-registration (e.g. returning for Master&apos;s)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    To reactivate this member, change Status to <strong>Active</strong> and set the new Expected month/year of completing school above. Then save.
                  </Typography>
                </Paper>
              </Grid>
            )}
            {id && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BadgeIcon fontSize="small" /> ID Card
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Chip
                      label={cardStatus === 'approved' ? 'Approved' : (cardStatus === 'pending' ? 'Pending' : 'No card record')}
                      color={cardStatus === 'approved' ? 'success' : 'default'}
                      size="small"
                    />
                    {!cardStatus && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<BadgeIcon />}
                        onClick={handleCreateCardRecord}
                        disabled={creatingCard}
                      >
                        {creatingCard ? 'Creating...' : 'Create card record'}
                      </Button>
                    )}
                    {cardStatus === 'pending' && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<BadgeIcon />}
                        onClick={handleApproveCard}
                        disabled={approvingCard}
                      >
                        {approvingCard ? 'Approving...' : 'Approve ID Card'}
                      </Button>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    ID card is automatically created and approved when you approve a member (set status to Active). Manual buttons available as fallback.
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard/members')}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}
